import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Ionicons, Trash2 } from './icons';
import { CvMiniature } from './CvMiniature';
import { ScoreRing } from './ScoreRing';
import { VignetteCv } from './VignetteCv';
import { t } from '../i18n';
import { useProfile } from '../hooks/useProfile';
import { colors, formes, layout, radius, shadow, spacing, typography } from '../theme/tokens';
import { savedCvToGeneratedCv } from '../utils/savedCv';
import { formatSavedDate } from '../utils/cvDates';
import type { SavedCv } from '../services/cv';

export interface CvListeLargeProps {
  cvs: SavedCv[];
  /** CV affiché d'abord : le principal, ou à défaut le plus récent. */
  principalId: string | undefined;
  onOuvrir: (cv: SavedCv, edit: boolean) => void;
  onPrincipal: (id: string) => void;
  onSupprimer: (cv: SavedCv) => void;
}

const FEUILLE_RATIO = 1.414;
const FEUILLE_MAX = 360;

function dateEtLangue(cv: SavedCv): string {
  return `${formatSavedDate(cv.created_at)} · ${(cv.output_language ?? 'fr').toUpperCase()}`;
}

function Ligne({ cv, choisi, onPress }: { cv: SavedCv; choisi: boolean; onPress: () => void }) {
  const [survol, setSurvol] = useState(false);
  return (
    <Pressable
      testID={`cvl-card-${cv.id}`}
      accessibilityRole="button"
      accessibilityState={{ selected: choisi }}
      accessibilityLabel={[
        cv.title,
        cv.is_primary ? t('cvList.primary') : null,
        dateEtLangue(cv),
        cv.ats_score !== null ? t('cvList.score', { score: cv.ats_score }) : null,
      ]
        .filter(Boolean)
        .join(', ')}
      onPress={onPress}
      onHoverIn={() => setSurvol(true)}
      onHoverOut={() => setSurvol(false)}
      style={[styles.ligne, survol && styles.ligneSurvol, choisi && styles.ligneChoisie]}
    >
      <VignetteCv template={cv.template} accentColor={cv.accent_color} taille="sm" />
      <View style={styles.ligneTextes}>
        <Text style={styles.ligneTitre} numberOfLines={2}>
          {cv.title}
        </Text>
        <Text style={styles.date}>{dateEtLangue(cv)}</Text>
      </View>
      {cv.is_primary ? <Ionicons name="star" size={16} color={colors.electricInk} /> : null}
      {cv.ats_score !== null ? (
        <View testID={`cvl-score-${cv.id}`} style={styles.pastilleScore}>
          <Text style={styles.pastilleScoreTexte}>{cv.ats_score}</Text>
        </View>
      ) : null}
    </Pressable>
  );
}

/**
 * Mes CV sur ordinateur : la liste à gauche, le CV choisi à droite, rendu d'après son vrai
 * contenu et non une vignette de traits. On compare ses CV en un clic, sans quitter la page ni la
 * faire défiler : seule la liste défile, dans son volet.
 */
export function CvListeLarge({ cvs, principalId, onOuvrir, onPrincipal, onSupprimer }: CvListeLargeProps) {
  const { profile } = useProfile();
  // Le CV enregistré ne porte pas le nom : on prend celui du profil, comme l'éditeur.
  const nom = [profile?.first_name, profile?.last_name].map((v) => v?.trim()).filter(Boolean).join(' ') || undefined;
  const [selId, setSelId] = useState<string | null>(null);
  const [hauteur, setHauteur] = useState(0);
  const choisi = cvs.find((c) => c.id === selId) ?? cvs.find((c) => c.id === principalId) ?? cvs[0];
  if (!choisi) return null;

  // La feuille prend toute la hauteur du volet, dans la limite d'une largeur lisible.
  const largeur = hauteur > 0 ? Math.round(Math.min(FEUILLE_MAX, (hauteur - spacing.xxl) / FEUILLE_RATIO)) : 320;
  const hauteurFeuille = Math.round(largeur * FEUILLE_RATIO);

  return (
    <View style={styles.racine} testID="cvl-deux-volets">
      <ScrollView style={styles.volet} contentContainerStyle={styles.liste} showsVerticalScrollIndicator={false}>
        {cvs.map((cv) => (
          <Ligne key={cv.id} cv={cv} choisi={cv.id === choisi.id} onPress={() => setSelId(cv.id)} />
        ))}
      </ScrollView>

      <View style={styles.fiche} onLayout={(e) => setHauteur(e.nativeEvent.layout.height)}>
        <View style={[styles.feuille, { width: largeur, height: hauteurFeuille }]}>
          <CvMiniature
            testID="cvl-apercu"
            cv={savedCvToGeneratedCv(choisi)}
            name={nom}
            title={choisi.title}
            template={choisi.template}
            accentColor={choisi.accent_color ?? undefined}
            width={largeur}
            height={hauteurFeuille}
          />
        </View>

        <View style={styles.infos}>
          <View style={styles.badges}>
            {choisi.is_primary ? (
              <View testID={`cvl-primary-${choisi.id}`} style={styles.badgePrincipal}>
                <Ionicons name="star" size={12} color={colors.electricInk} />
                <Text style={styles.badgePrincipalTexte}>{t('cvList.primary')}</Text>
              </View>
            ) : null}
            <Text testID={`cvl-lang-${choisi.id}`} style={styles.date}>
              {dateEtLangue(choisi)}
            </Text>
          </View>
          <Text style={styles.titre} numberOfLines={4}>
            {choisi.title}
          </Text>

          {choisi.ats_score !== null ? (
            <View style={styles.score}>
              <ScoreRing score={choisi.ats_score} size={spacing.xxl * 3} variante="degrade" />
              <Text style={styles.scoreTexte}>{t('cvList.score', { score: choisi.ats_score })}</Text>
            </View>
          ) : null}

          <View style={styles.actions}>
            <Pressable
              testID={`cvl-edit-${choisi.id}`}
              accessibilityRole="button"
              onPress={() => onOuvrir(choisi, true)}
              style={[styles.bouton, styles.boutonSombre]}
            >
              <Ionicons name="pencil" size={15} color={colors.white} />
              <Text style={[styles.boutonTexte, { color: colors.white }]}>{t('cvList.edit')}</Text>
            </Pressable>
            <Pressable
              testID={`cvl-preview-${choisi.id}`}
              accessibilityRole="button"
              onPress={() => onOuvrir(choisi, false)}
              style={[styles.bouton, styles.boutonClair]}
            >
              <Ionicons name="eye-outline" size={16} color={colors.electricInk} />
              <Text style={[styles.boutonTexte, { color: colors.electricInk }]}>{t('cvList.preview')}</Text>
            </Pressable>
            {!choisi.is_primary ? (
              <Pressable
                testID={`cvl-setprimary-${choisi.id}`}
                accessibilityRole="button"
                onPress={() => onPrincipal(choisi.id)}
                style={styles.lien}
              >
                <Text style={styles.lienTexte}>{t('cvList.setPrimary')}</Text>
              </Pressable>
            ) : null}
            <Pressable
              testID={`cvl-delete-${choisi.id}`}
              accessibilityRole="button"
              accessibilityLabel={t('common.delete')}
              onPress={() => onSupprimer(choisi)}
              style={styles.lien}
            >
              <Trash2 size={18} color={colors.error} strokeWidth={2} />
              <Text style={[styles.lienTexte, { color: colors.error }]}>{t('common.delete')}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  racine: {
    flex: 1,
    flexDirection: 'row',
    gap: spacing.xl,
    minHeight: 0,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    paddingHorizontal: spacing.xxl + spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
  },
  volet: { width: layout.voletPrincipalLargeur, flexGrow: 0, flexShrink: 0 },
  liste: { gap: spacing.sm },

  ligne: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: 'transparent',
    cursor: 'pointer',
    ...shadow.card,
  },
  ligneSurvol: { borderColor: colors.bordureClaire },
  ligneChoisie: { borderColor: colors.electricDeep, backgroundColor: colors.blueSoft },
  ligneTextes: { flex: 1, minWidth: 0, gap: 2 },
  ligneTitre: { fontSize: typography.bodyMd.size, lineHeight: typography.bodyMd.lineHeight, fontWeight: '800', color: colors.night },
  date: { fontSize: typography.small.size, color: colors.grey500 },
  pastilleScore: {
    minWidth: 34,
    height: 24,
    paddingHorizontal: spacing.sm,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.blueSoft,
  },
  pastilleScoreTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.electricInk },

  fiche: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xl },
  feuille: { ...shadow.floating, borderRadius: radius.sm, backgroundColor: colors.white },
  infos: { flex: 1, minWidth: 0, gap: spacing.lg },
  badges: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm },
  badgePrincipal: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: radius.pill,
    backgroundColor: colors.blueSoft,
  },
  badgePrincipalTexte: { fontSize: typography.caption.size, fontWeight: '800', color: colors.electricInk },
  titre: { fontSize: typography.h1.size, lineHeight: typography.h1.lineHeight, fontWeight: '800', color: colors.night },
  score: { flexDirection: 'row', alignItems: 'center', gap: spacing.lg },
  scoreTexte: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  actions: { gap: spacing.sm, alignItems: 'stretch', maxWidth: layout.controlMaxWidth },
  bouton: {
    minHeight: formes.cible,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  boutonSombre: { backgroundColor: colors.night },
  boutonClair: { backgroundColor: colors.white, borderWidth: 1.5, borderColor: colors.bordureClaire },
  boutonTexte: { fontSize: typography.bodyMd.size, fontWeight: '800' },
  lien: {
    minHeight: formes.cible,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    cursor: 'pointer',
  },
  lienTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.electricInk },
});
